import { useState, useEffect, useCallback, useRef } from "react";
import Papa from "papaparse";
import { useClub } from "@/pages/ClubLayout";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Users, Search, Upload, X, Pencil, FileSpreadsheet, Download, Trash2, GitMerge } from "lucide-react";

const AUTH = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
});

export default function ClubPlayers() {
  const ctx = useClub();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editPlayer, setEditPlayer] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showMerge, setShowMerge] = useState(false);

  const fetchPlayers = useCallback(async () => {
    try {
      let url = `${API}/club/players?`;
      if (search) url += `q=${encodeURIComponent(search)}&`;
      if (statusFilter !== "all") url += `status=${statusFilter}&`;
      const res = await fetch(url, { credentials: "include", headers: AUTH() });
      if (res.ok) setPlayers(await res.json());
    } catch {
      toast.error("Error al cargar jugadores");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

  const openEdit = (p) => {
    setEditPlayer(p);
    setShowForm(true);
  };

  const closeForm = () => {
    setEditPlayer(null);
    setShowForm(false);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === players.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(players.map((p) => p.member_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminar ${selected.size} jugador(es)?`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/club/players/bulk-delete`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({ member_ids: Array.from(selected) }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.deleted} jugador(es) eliminados`);
        setSelected(new Set());
        fetchPlayers();
      } else {
        throw new Error("Error");
      }
    } catch {
      toast.error("Error al eliminar jugadores");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="club-players-title">
            <Users className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            Jugadores
          </h1>
          <p className="text-sm text-[#64748B] mt-1">{ctx?.club?.name} — {players.length} jugador{players.length !== 1 ? "es" : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-players-btn">
            <Upload className="w-4 h-4 mr-2" /> Importar
          </Button>
          <Button onClick={() => { closeForm(); setShowForm(true); }} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white" data-testid="add-player-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Jugador
          </Button>
        </div>
      </div>

      {/* Search & filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email o no. socio..."
            className="pl-10"
            data-testid="player-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="player-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-red-700">{selected.size} jugador{selected.size !== 1 ? "es" : ""} seleccionado{selected.size !== 1 ? "s" : ""}</span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="text-[#64748B]">
              Deseleccionar
            </Button>
            {selected.size >= 2 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowMerge(true)}
                className="border-[#1B4D3E] text-[#1B4D3E] hover:bg-[#1B4D3E]/5"
                data-testid="merge-players-btn"
              >
                <GitMerge className="w-3.5 h-3.5 mr-1" />
                Fusionar
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleBulkDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              {deleting ? "Eliminando..." : "Eliminar Seleccionados"}
            </Button>
          </div>
        </div>
      )}

      {/* Merge modal */}
      {showMerge && (
        <MergeModal
          players={players.filter((p) => selected.has(p.member_id))}
          onClose={() => setShowMerge(false)}
          onMerged={() => { setShowMerge(false); setSelected(new Set()); fetchPlayers(); }}
        />
      )}

      {/* Player form modal */}
      {showForm && (
        <PlayerForm
          player={editPlayer}
          onClose={closeForm}
          onSaved={() => { closeForm(); fetchPlayers(); }}
        />
      )}

      {/* Import modal */}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); fetchPlayers(); }}
        />
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : players.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-16 text-center">
            <Users className="w-12 h-12 text-[#64748B] mx-auto mb-4 opacity-40" />
            <p className="text-[#64748B] font-medium mb-1">No hay jugadores registrados</p>
            <p className="text-sm text-[#94A3B8] mb-4">Agrega jugadores manualmente o importa un archivo</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <Upload className="w-4 h-4 mr-2" /> Importar
              </Button>
              <Button onClick={() => setShowForm(true)} className="bg-[#1B4D3E] hover:bg-[#163F33] text-white">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Jugador
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="card-default">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="players-table">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={players.length > 0 && selected.size === players.length}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-[#1B4D3E] focus:ring-[#1B4D3E]"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Jugador</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">No. Socio</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Index</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Contacto</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748B] uppercase">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p) => (
                    <tr
                      key={p.member_id}
                      className={`border-b border-gray-50 hover:bg-gray-50/50 ${selected.has(p.member_id) ? "bg-red-50/40" : ""}`}
                      data-testid={`player-row-${p.member_id}`}
                    >
                      <td className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={selected.has(p.member_id)}
                          onChange={() => toggleSelect(p.member_id)}
                          className="w-4 h-4 rounded border-gray-300 text-[#1B4D3E] focus:ring-[#1B4D3E]"
                          data-testid={`select-player-${p.member_id}`}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[#0F172A]">{p.first_name} {p.last_name}</p>
                      </td>
                      <td className="px-4 py-3 text-[#64748B] font-mono text-xs">{p.member_number || "-"}</td>
                      <td className="px-4 py-3">
                        {p.handicap_index != null ? (
                          <span className={`font-semibold ${p.handicap_index < 0 ? "text-blue-600" : "text-[#0F172A]"}`}>
                            {p.handicap_index < 0 ? `+${Math.abs(p.handicap_index)}` : p.handicap_index}
                          </span>
                        ) : (
                          <span className="text-[#94A3B8]">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#64748B] text-xs">
                        {p.email && <div>{p.email}</div>}
                        {p.phone && <div>{p.phone}</div>}
                        {!p.email && !p.phone && "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${p.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {p.status === "active" ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-player-${p.member_id}`}>
                          <Pencil className="w-4 h-4 text-[#64748B]" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ==========================================================================
// Player Form Modal (create / edit)
// ==========================================================================

function MergeModal({ players, onClose, onMerged }) {
  const [survivorId, setSurvivorId] = useState(players[0]?.member_id || "");
  const [merging, setMerging] = useState(false);

  const handleMerge = async () => {
    if (!survivorId) return toast.error("Selecciona el jugador principal");
    setMerging(true);
    try {
      const res = await fetch(`${API}/club/players/merge`, {
        method: "POST", credentials: "include", headers: AUTH(),
        body: JSON.stringify({
          survivor_id: survivorId,
          duplicate_ids: players.filter((p) => p.member_id !== survivorId).map((p) => p.member_id),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      const moved = data.enrollments_moved + data.results_moved + data.scores_moved + data.tournaments_moved;
      toast.success(`${data.merged} perfil(es) fusionado(s). ${moved} registro(s) reasignado(s).`);
      onMerged();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al fusionar jugadores");
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="merge-players-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-[#1B4D3E]" /> Fusionar Jugadores
          </h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" data-testid="merge-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#64748B] mb-4">
          Selecciona el perfil <strong>principal</strong> que se conservará. Los demás perfiles serán eliminados y todos sus datos (inscripciones, resultados, scores y torneos) se reasignarán al principal.
        </p>
        <div className="space-y-2 mb-5 max-h-64 overflow-y-auto">
          {players.map((p) => (
            <label
              key={p.member_id}
              className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${survivorId === p.member_id ? "border-[#1B4D3E] bg-[#1B4D3E]/5" : "border-gray-200 hover:border-gray-300"}`}
              data-testid={`merge-survivor-option-${p.member_id}`}
            >
              <input
                type="radio"
                name="survivor"
                checked={survivorId === p.member_id}
                onChange={() => setSurvivorId(p.member_id)}
                className="accent-[#1B4D3E]"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{p.first_name} {p.last_name}</p>
                <p className="text-xs text-[#94A3B8] truncate">
                  {p.email || "Sin email"}{p.member_number ? ` · #${p.member_number}` : ""}{p.handicap_index != null ? ` · HCP ${p.handicap_index}` : ""}
                </p>
              </div>
              {survivorId === p.member_id && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#1B4D3E] bg-[#1B4D3E]/10 px-2 py-0.5 rounded-full">Principal</span>
              )}
            </label>
          ))}
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          <p className="text-xs text-amber-700">Esta acción no se puede deshacer. Los perfiles duplicados serán eliminados permanentemente.</p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={merging}>Cancelar</Button>
          <Button
            onClick={handleMerge}
            disabled={merging}
            className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
            data-testid="confirm-merge-btn"
          >
            {merging ? "Fusionando..." : `Fusionar ${players.length} perfiles`}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PlayerForm({ player, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: player?.first_name || "",
    last_name: player?.last_name || "",
    email: player?.email || "",
    phone: player?.phone || "",
    member_number: player?.member_number || "",
    ghin_number: player?.ghin_number || "",
    handicap_index: player?.handicap_index != null ? String(player.handicap_index) : "",
    notes: player?.notes || "",
    status: player?.status || "active",
    sex: player?.sex || "",
    is_senior: !!player?.is_senior,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      return toast.error("Nombre y apellido son requeridos");
    }
    setSaving(true);
    try {
      const url = player ? `${API}/club/players/${player.member_id}` : `${API}/club/players`;
      const method = player ? "PUT" : "POST";
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        member_number: form.member_number.trim() || null,
        ghin_number: form.ghin_number.trim() || null,
        handicap_index: form.handicap_index.trim() || null,
        notes: form.notes.trim() || null,
        status: form.status,
        sex: form.sex || null,
        is_senior: !!form.is_senior,
      };
      const res = await fetch(url, { method, credentials: "include", headers: AUTH(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      toast.success(player ? "Jugador actualizado" : "Jugador creado");
      onSaved();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="player-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-bold text-[#0F172A]">{player ? "Editar Jugador" : "Nuevo Jugador"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Nombre *</Label>
              <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="Nombre" data-testid="player-first-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Apellido *</Label>
              <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Apellido" data-testid="player-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="correo@ejemplo.com" data-testid="player-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Telefono</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+52 33 1234 5678" data-testid="player-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">No. Socio</Label>
              <Input value={form.member_number} onChange={(e) => set("member_number", e.target.value)} placeholder="Ej: 1234" data-testid="player-member-number" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Handicap Index</Label>
              <Input value={form.handicap_index} onChange={(e) => set("handicap_index", e.target.value)} placeholder="Ej: 12.5 o +2.3" data-testid="player-handicap-index" />
              <p className="text-xs text-[#94A3B8]">Decimal. Usa + para plus handicap</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">GHIN Number</Label>
              <Input value={form.ghin_number} onChange={(e) => set("ghin_number", e.target.value)} placeholder="Ej: 1234567" data-testid="player-ghin-number" />
              <p className="text-xs text-[#94A3B8]">Opcional. Número USGA/GHIN del jugador</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Sexo</Label>
              <Select value={form.sex || "none"} onValueChange={(v) => set("sex", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="player-sex-select">
                  <SelectValue placeholder="Sin especificar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin especificar</SelectItem>
                  <SelectItem value="M">Masculino</SelectItem>
                  <SelectItem value="F">Femenino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Categoria de edad</Label>
              <button
                type="button"
                data-testid="player-senior-toggle"
                onClick={() => set("is_senior", !form.is_senior)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm ${form.is_senior ? "bg-[#1B4D3E] text-white border-[#1B4D3E]" : "bg-white text-[#0F172A] border-gray-200"}`}
              >
                <span>Senior</span>
                <span className={`w-9 h-5 rounded-full flex items-center transition-colors ${form.is_senior ? "bg-white/25 justify-end" : "bg-gray-200 justify-start"} px-0.5`}>
                  <span className={`w-4 h-4 rounded-full ${form.is_senior ? "bg-white" : "bg-white shadow"}`} />
                </span>
              </button>
              <p className="text-xs text-[#94A3B8]">Marca si el jugador es senior</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Observaciones</Label>
            <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Notas opcionales" data-testid="player-notes" />
          </div>
          {player && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="player-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancelar</Button>
          <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleSave} disabled={saving} data-testid="player-save-btn">
            {saving ? "Guardando..." : player ? "Guardar Cambios" : "Crear Jugador"}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ==========================================================================
// Import Modal (CSV paste or JSON)
// ==========================================================================

function ImportModal({ onClose, onImported }) {
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result || "");
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    // Use Papaparse (RFC-4180) so quoted commas and decimal-comma values are handled.
    const parsed = Papa.parse(text.trim(), { header: true, skipEmptyLines: true });
    return (parsed.data || []).map((raw) => {
      const obj = {};
      for (const rawKey of Object.keys(raw)) {
        const h = String(rawKey || "").trim().toLowerCase().replace(/\s+/g, "_");
        // Map common header names (English + Spanish aliases).
        const key =
          h === "nombre" ? "first_name"
          : h === "apellido" ? "last_name"
          : h === "index" ? "handicap_index"
          : (h === "socio" || h === "no_socio" || h === "numero_socio") ? "member_number"
          : h === "correo" ? "email"
          : h === "telefono" ? "phone"
          : (h === "notas" || h === "observaciones") ? "notes"
          : (h === "sexo" || h === "genero") ? "sex"
          : (h === "senior" || h === "es_senior") ? "is_senior"
          : h;
        let v = (raw[rawKey] ?? "").toString().trim();
        if (key === "handicap_index") v = v.replace(",", ".").replace(/^\+/, "-");
        // Normalize sex to M/F: accept "Masculino/Femenino", "Hombre/Mujer", "M/F".
        if (key === "sex") {
          const up = v.toUpperCase();
          if (up.startsWith("M") || up.startsWith("H")) v = "M";
          else if (up.startsWith("F") || up.startsWith("MU")) v = "F";
          else v = "";
        }
        // Normalize senior to boolean strings the backend understands.
        if (key === "is_senior") {
          const low = v.toLowerCase();
          v = ["1", "true", "si", "sí", "yes", "y", "x", "senior"].includes(low) ? "true" : "";
        }
        obj[key] = v;
      }
      return obj;
    });
  };

  const handleImport = async () => {
    if (!csvText.trim()) return toast.error("Pega o carga datos CSV");
    const players = parseCSV(csvText);
    if (players.length === 0) return toast.error("No se encontraron datos validos");

    setImporting(true);
    try {
      const res = await fetch(`${API}/club/players/import`, {
        method: "POST",
        credentials: "include",
        headers: AUTH(),
        body: JSON.stringify({ players }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      const data = await res.json();
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} jugador(es) importados`);
      }
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-[#0F172A] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#1B4D3E]" /> Importar Jugadores
          </h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#64748B]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-[#64748B] mb-2">
              Descarga la plantilla, llena los datos y sube el archivo.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const csv = "first_name,last_name,email,phone,member_number,handicap_index,sex,is_senior\nJuan,Perez,juan@email.com,+52 33 1234 5678,1001,15.2,M,false\nMaria,Garcia,maria@email.com,,1002,22.1,F,true\n";
                const blob = new Blob([csv], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "plantilla_jugadores.csv";
                a.click();
                URL.revokeObjectURL(url);
              }}
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Descargar Plantilla CSV
            </Button>
            <p className="text-xs text-[#94A3B8] mt-2">
              Encabezados aceptados (esp/ing): nombre, apellido, correo, telefono, socio, index, sexo (M/F/Masculino/Femenino), senior (true/false/si)
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Cargar archivo CSV</Label>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} className="text-sm" data-testid="csv-file-input" />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">O pegar datos CSV</Label>
            <textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              rows={8}
              className="w-full border border-gray-200 rounded-lg p-3 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/30"
              placeholder={`first_name,last_name,email,handicap_index,member_number,sex,is_senior\nJuan,Perez,juan@email.com,15.2,1001,M,false\nMaria,Garcia,maria@email.com,22.1,1002,F,true`}
              data-testid="csv-textarea"
            />
          </div>

          {result && (
            <div className="bg-gray-50 rounded-lg p-4 text-sm" data-testid="import-result">
              <p className="font-medium text-[#0F172A]">Resultado:</p>
              <p className="text-green-600">{result.created} jugador(es) creados</p>
              {result.skipped > 0 && <p className="text-amber-600">{result.skipped} omitidos (duplicados o datos invalidos)</p>}
              {result.errors?.length > 0 && (
                <div className="mt-2 text-red-600 text-xs">{result.errors.map((e, i) => <p key={i}>{e}</p>)}</div>
              )}
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={result ? onImported : onClose}>
            {result ? "Cerrar" : "Cancelar"}
          </Button>
          {!result && (
            <Button className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white" onClick={handleImport} disabled={importing} data-testid="import-submit-btn">
              {importing ? "Importando..." : "Importar"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
